import type { ReactNode } from 'react'

export function Panel({ title, action, children }: { title?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded border border-line bg-panel shadow-sm">
      {title && (
        <header className="flex items-center justify-between border-b border-line px-5 py-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-3">{title}</h2>
          {action}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  )
}

const PILL: Record<string, string> = {
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  crit: 'bg-crit-soft text-crit',
  neutral: 'bg-ground text-ink-3',
  accent: 'bg-accent-soft text-accent',
}

export function Pill({ tone = 'neutral', children }: { tone?: keyof typeof PILL; children: ReactNode }) {
  return (
    <span
      className={`inline-block rounded-sm px-2 py-[2px] font-mono text-[10px] font-bold uppercase tracking-wider ${PILL[tone]}`}
    >
      {children}
    </span>
  )
}

/** Estado de energia com cor semantica — legivel de relance na lista. */
export function PowerPill({ state }: { state: string }) {
  const tone = state === 'on' ? 'ok' : state === 'off' ? 'crit' : state === 'transitioning' ? 'warn' : 'neutral'
  const label =
    state === 'on' ? 'ligado' : state === 'off' ? 'desligado' : state === 'transitioning' ? 'mudando' : 'desconhecido'
  return <Pill tone={tone as keyof typeof PILL}>{label}</Pill>
}

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled,
  type = 'button',
  title,
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'default' | 'primary' | 'danger' | 'ghost'
  disabled?: boolean
  type?: 'button' | 'submit'
  title?: string
}) {
  const styles = {
    default: 'border-line bg-panel text-ink hover:border-accent hover:text-accent',
    primary: 'border-accent bg-accent text-white hover:opacity-90',
    danger: 'border-crit bg-crit-soft text-crit hover:bg-crit hover:text-white',
    ghost: 'border-transparent bg-transparent text-ink-3 hover:text-ink',
  }[variant]
  return (
    <button
      type={type}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`rounded border px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${styles}`}
    >
      {children}
    </button>
  )
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-3">{hint}</span>}
    </label>
  )
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded border border-line bg-panel px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none ${props.className ?? ''}`}
    />
  )
}

export function Alert({ tone = 'crit', children }: { tone?: 'crit' | 'warn' | 'ok'; children: ReactNode }) {
  const map = {
    crit: 'border-l-crit bg-crit-soft text-crit',
    warn: 'border-l-warn bg-warn-soft text-warn',
    ok: 'border-l-ok bg-ok-soft text-ok',
  }[tone]
  return <div className={`border-l-[3px] px-4 py-3 text-sm ${map}`}>{children}</div>
}
